import React, { useContext, useEffect, useState } from 'react';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { transformAttributeValue } from '../../common/utils/factAttributeUtils';
import type { FactAttributeDetail } from '../../common/utils/factAttributeUtils';

import { useAccess } from '@/common/hooks/useAccess';
import { RootState } from '../../app/rootReducer';
import { canViewPIIData } from '../../common/actionHelper';
import { getApplicationPIIDataEndPoint } from '../../common/api.endPoints';
import { Product } from '../../common/components/Product';
import { FlexRow, NoraButton, TitleSmall } from '../../common/components/v2';
import { customCitizenshipDisplay } from '../../common/constants/citizenship';
import {
  DecisionType,
  transformRateClass,
  transformTobaccoClass,
} from '../../common/constants/decision';
import { Resource } from '../../common/constants/resource';
import {
  isNonCarrierRolePresent,
  NoraPermissionV2,
} from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import ElementLocators from '../../common/ElementLocators';
import { getFormattedTimestamp } from '../../common/helpers';
import { HttpService } from '../../common/http.service';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import { ProductFactory } from '../../common/productConfig/productFactory';
import {
  getOwnerDependentPolicies,
  getOwnerProfile,
  OwnerLinkedPoliciesResponse,
  ProfilePersonResponse,
} from '../../common/services/ownerProfileService';
import { isTheBoolEmpty } from '../../common/utilities';
import { SnackbarContext } from '../layout/SnackbarContext';
import { AuthContext } from '../login/AuthWrapper';
import { ITask } from '../tasks/interfaces/ITask';
import { getAllTasks } from '../tasks/slice/taskListSlice';
import { TaskTypes } from '../tasks/types/taskTypes';
import { BeneficiaryDetailsDto } from './detailSlice';
import { PolicyStatus } from './policyStatus/PolicyStatus';
import styles from './styles.module.scss';
import {
  booleanToYesNo,
  capitalizeFirstLetter,
  dateToString,
  DATE_FORMAT_ONLY_MONTH_AND_YEAR,
  formatInsuredAge,
  getAge,
} from './utilities/formattingUtilities';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const getDependentStatusColor = (status: string | undefined): string => {
  if (!status) return '#2C2C2A';
  if (['PREMIUM_PAYING', 'IN_FORCE'].includes(status)) return '#27500A';
  if (['LAPSED', 'CLOSED', 'DECLINED'].includes(status)) return '#791F1F';
  return '#2C2C2A';
};

const DependentDecisionBadge = ({ decision }: { decision: string | undefined }) => {
  if (!decision) return <Typography variant="body2">{Resource.NORA_M_DASH}</Typography>;
  if (decision === 'Declined') {
    return <span style={{ display: 'inline-flex', fontSize: 12, padding: '2px 8px', borderRadius: 8, lineHeight: 1.4, background: '#FCEBEB', color: '#791F1F' }}>{decision}</span>;
  }
  if (decision === 'RUW') {
    return <span style={{ display: 'inline-flex', fontSize: 12, padding: '2px 8px', borderRadius: 8, lineHeight: 1.4, background: '#FAEEDA', color: '#854F0B' }}>{decision}</span>;
  }
  if (decision === 'PIA') {
    return <span style={{ display: 'inline-flex', fontSize: 12, padding: '2px 8px', borderRadius: 8, lineHeight: 1.4, background: '#E3ECF8', color: '#1A4B8C' }}>{decision}</span>;
  }
  if (decision.startsWith('Standard')) {
    return <span style={{ display: 'inline-flex', fontSize: 12, padding: '2px 8px', borderRadius: 8, lineHeight: 1.4, background: '#EAF3DE', color: '#27500A' }}>{decision}</span>;
  }
  return <Typography variant="body2">{decision}</Typography>;
};

const OwnerOverviewFailure = () => (
  <div className={styles.Section}>
    <Typography color="error">Unable to load owner overview.</Typography>
  </div>
);

export const OwnerPersonalInfoOverview = ({
  applicationId,
}: {
  applicationId: number;
}) => {
  const [owner, setOwner] = useState<ProfilePersonResponse | null>(null);
  const [ownerLinkedPolicies, setOwnerLinkedPolicies] =
    useState<OwnerLinkedPoliciesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOwnerPII, setShowOwnerPII] = useState(false);
  const canViewPii = useAccess({
    permission: NoraPermissionV2.VIEW_PII_DATA,
    legacyCheck: canViewPIIData,
  });
  const { income, householdIncome } = useSelector(
    (state: RootState) => ({
      income: state.application.income,
      householdIncome: state.application.householdIncome,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (applicationId) {
      const id = String(applicationId);
      Promise.all([
        getOwnerProfile(id),
        getOwnerDependentPolicies(id),
      ])
        .then(([profile, linked]) => {
          setOwner(profile);
          setOwnerLinkedPolicies(linked);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [applicationId]);

  if (isLoading) {
    return <CircularProgress />;
  }
  if (!owner) {
    return <OwnerOverviewFailure />;
  }

  const fullName = [owner.nameFirst, owner.nameMiddle, owner.nameLast]
    .filter(Boolean)
    .join(' ');
  const address = owner.addresses?.[0];
  const addressStr = address
    ? `${address.street1}, ${address.city}, ${address.state} ${address.zip}`
    : Resource.NORA_M_DASH;
  const email =
    owner.emails?.find(e => e.emailType === 'primary')?.emailAddress ??
    owner.emails?.[0]?.emailAddress ??
    Resource.NORA_M_DASH;
  const phone = owner.phones?.[0]?.phoneNumber ?? Resource.NORA_M_DASH;

  const linkedPolicies = (() => {
    if (!ownerLinkedPolicies) return [];
    const activatedPolicyIds = new Set<number>();
    const rows: {
      key: string;
      carrierPolicyNumber: string | undefined;
      policyId: number | undefined;
      applicationId: number | undefined;
      insuredBirthDate: string | undefined;
      coverage: number | undefined;
      decision: string | undefined;
      status: string | undefined;
      isCurrentApp: boolean;
    }[] = [];
    for (const p of ownerLinkedPolicies.activatedPolicies) {
      if (p.termPolicyId != null) activatedPolicyIds.add(p.termPolicyId);
      rows.push({
        key: p.carrierPolicyNumber,
        carrierPolicyNumber: p.carrierPolicyNumber,
        policyId: p.termPolicyId,
        applicationId: p.applicationId,
        insuredBirthDate: p.insuredBirthDate,
        coverage: p.coverage,
        decision: p.detailedLifeCycleV2,
        status: p.status,
        isCurrentApp: p.applicationId === applicationId,
      });
    }
    for (const p of ownerLinkedPolicies.maestroPolicies) {
      if (activatedPolicyIds.has(p.policyId)) continue;
      rows.push({
        key: p.carrierPolicyNumber ?? String(p.policyId),
        carrierPolicyNumber: p.carrierPolicyNumber,
        policyId: p.policyId,
        applicationId: p.applicationId,
        insuredBirthDate: p.insuredBirthDate,
        coverage: p.coverage,
        decision: p.detailedLifeCycleV2,
        status: undefined,
        isCurrentApp: p.applicationId === applicationId,
      });
    }
    return rows;
  })();

  return (
    <div
      className={styles.Section}
      data-label={'overview_section'}
      data-section-id={'OwnerOverview'}
      id="OwnerPerInfo"
    >
      <FlexRow className={styles.Title}>
        <TitleSmall>{'Personal Information (Owner)'}</TitleSmall>
      </FlexRow>

      <OverviewRow label={'Name'} info={fullName} />
      <OverviewRow
        label={'Birthdate'}
        info={dateToString(owner.birthDate) || Resource.NORA_M_DASH}
      />
      <OverviewRow
        label={'Gender'}
        info={owner.gender || Resource.NORA_M_DASH}
      />
      <OverviewRow label={'Address'} info={addressStr} />
      {linkedPolicies.length > 0 && (
        <div className={styles.Section}>
          <OverviewRow label={'Dependents'} info={`${linkedPolicies.length} linked`} />
          <div style={{ borderRadius: 8, border: '0.5px solid #D3D1C7', marginBottom: 14, width: '100%' }}>
            <Table size="small" style={{ background: '#F1EFE8', width: '100%' }}>
              <TableBody>
                <TableRow>
                  {['Policy Number', 'Age', 'Coverage', 'Detailed Lifecycle', 'Policy status'].map(h => (
                    <TableCell key={h} style={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" color="text.secondary">{h}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                {linkedPolicies.map(policy => (
                  <TableRow key={policy.key}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {policy.applicationId ? (
                          <Link
                            component={RouterLink}
                            to={`/workbench/applications/${policy.applicationId}`}
                            target="_blank"
                          >
                            {policy.carrierPolicyNumber ?? policy.policyId}
                          </Link>
                        ) : policy.policyId ? (
                          <Link
                            component={RouterLink}
                            to={`/workbench/ppwb/policy/${policy.policyId}`}
                            target="_blank"
                          >
                            {policy.carrierPolicyNumber ?? policy.policyId}
                          </Link>
                        ) : (
                          <Typography variant="body2">{policy.carrierPolicyNumber ?? Resource.NORA_M_DASH}</Typography>
                        )}
                        {policy.isCurrentApp && (
                          <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: '#E3ECF8', color: '#1A4B8C', whiteSpace: 'nowrap' }}>
                            Current
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {policy.insuredBirthDate
                          ? formatInsuredAge(policy.insuredBirthDate)
                          : Resource.NORA_M_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {policy.coverage != null
                          ? `$${policy.coverage.toLocaleString()}`
                          : Resource.NORA_M_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <DependentDecisionBadge decision={policy.decision} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ color: getDependentStatusColor(policy.status) }}>
                        {policy.status ?? Resource.NORA_M_DASH}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <OverviewRow
        label={'Personal Income'}
        info={getIncome(income) || Resource.NORA_M_DASH}
      />
      {householdIncome ? (
        <OverviewRow
          label={'Household Income'}
          info={getIncome(householdIncome) || Resource.NORA_M_DASH}
        />
      ) : null}

      {canViewPii && (
        <div className={styles.Section}>
          <OverviewRow
            className={styles.Title}
            label={Resource.TASK_TITLE_PII_DATA}
          >
            <NoraButton
              type="button"
              name={ElementLocators.LOCATOR_SHOW_OWNER_PII_DATA_BUTTON}
              onClick={() => setShowOwnerPII(prev => !prev)}
            >
              {showOwnerPII ? Resource.HIDE_PII : Resource.SHOW_PII}
            </NoraButton>
          </OverviewRow>
          <OverviewRow
            label={'Email'}
            info={showOwnerPII ? email : Resource.HIDDEN_STRING_TEXT}
          />
          <OverviewRow
            label={'Phone'}
            info={showOwnerPII ? phone : Resource.HIDDEN_STRING_TEXT}
          />
        </div>
      )}
    </div>
  );
};

export const Overview = () => {
  const { product, subCategory, carrier, applicationId } = useSelector(
    (state: RootState) => ({
      product: state.application.product,
      subCategory: state.application.subCategory,
      carrier: state.application.carrier,
      applicationId: state.application.applicationId,
    }),
    shallowEqual,
  );
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory ?? '',
  );
  const shouldRenderOwnerVariant =
    productConfig?.shouldRenderOwnerOverview() ?? false;

  if (!shouldRenderOwnerVariant) {
    return (
      <div className={styles.Container}>
        <FlexRow className={styles.SectionHeader}>
          <TitleSmall>{'Overview'}</TitleSmall>
        </FlexRow>
        <div className={styles.OverviewContainer}>
          <div className={styles.SectionContainer}>
            <PersonalInfoOverview />
          </div>
          <div className={styles.SectionContainer}>
            <PolicyInfoOverview />
          </div>
          <div className={styles.SectionContainer}>
            <RiskInfoOverview />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <FlexRow className={styles.SectionHeader}>
        <TitleSmall>{'Overview - Insured'}</TitleSmall>
      </FlexRow>
      <div className={styles.OverviewContainer}>
        <div className={styles.SectionContainer}>
          <PersonalInfoOverview isOwnerVariant={true} />
        </div>
        <div className={styles.SectionContainer}>
          <PolicyInfoOverview />
        </div>
        <div className={styles.SectionContainer}>
          <RiskInfoOverview />
        </div>
      </div>

      <FlexRow className={styles.OwnerSectionHeader}>
        <TitleSmall>{'Overview - Owner'}</TitleSmall>
      </FlexRow>
      <div className={styles.OverviewContainer}>
        <div className={styles.SectionContainer} style={{ gridColumn: '1 / -1' }}>
          <OwnerPersonalInfoOverview applicationId={applicationId} />
        </div>
      </div>
    </div>
  );
};

export interface PIIData {
  ssn: string;
  driverLicenseNumber: string;
  driverLicenseState: string;
  decryptedItin: string;
  emailAddress: string;
  phoneNumber: string;
}

export interface IdentificationDocLinkResponse {
  identificationDocLink?: string | null;
}

export const OverviewRow = ({
  label,
  info,
  children,
  className,
}: {
  label: string;
  info?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) => (
  <div className={`${styles.Row} ${className}`}>
    <div className={styles.SingleLineLabel}>{label}</div>
    <div className={styles.RightAlignedInfo}>{info || children}</div>
  </div>
);

type RiskDetailItem = { label: string; value: string };

const isValuePresent = (
  value: string | number | boolean | null | undefined,
): boolean => value !== null && value !== undefined && value !== '';

const formatRiskDetailValue = (
  value: string | number | boolean | null | undefined,
): string =>
  typeof value === 'boolean'
    ? booleanToYesNo(value)
    : transformAttributeValue(String(value));

const buildRiskDetailItems = (
  items: FactAttributeDetail[],
): RiskDetailItem[] =>
  items
    .filter(item => isValuePresent(item.value))
    .map(item => ({
      label: item.label,
      value: formatRiskDetailValue(item.value),
    }));

export const RiskDetailRow = ({
  label,
  value,
  details,
}: {
  label: string;
  value: boolean | undefined;
  details: RiskDetailItem[];
}) => {
  const [expanded, setExpanded] = useState(false);

  if (details.length === 0) {
    return <OverviewRow label={label} info={booleanToYesNo(value)} />;
  }

  return (
    <>
      <div
        className={`${styles.Row} ${styles.RiskDetailRow}`}
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className={styles.SingleLineLabel}>{label}</div>
        <div className={styles.RightAlignedInfo}>
          {booleanToYesNo(value)}
          <FontAwesomeIcon
            icon={faAngleDown}
            className={styles.RiskDetailChevron}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </div>
      </div>
      {expanded && (
        <div className={styles.RiskDetailPanel}>
          {details.map(item => (
            <OverviewRow
              key={item.label}
              label={item.label}
              info={item.value ?? Resource.NORA_M_DASH}
            />
          ))}
        </div>
      )}
    </>
  );
};

const ProductRow = ({
  label,
  product,
  carrier,
  subCategory,
  bundledPolicy,
}: {
  label: string;
  product: string;
  carrier: string;
  subCategory: string;
  bundledPolicy?: any;
}) => (
  <OverviewRow label={label}>
    <Product
      carrier={carrier}
      product={product}
      subCategory={subCategory}
    ></Product>
    {bundledPolicy && !bundledPolicy.isStacked && (
      <span>{` (${bundledPolicy.productId} - bundled purchase)`}</span>
    )}
  </OverviewRow>
);

const PIIDataRow = ({
  showInfo,
  setShowInfo,
  piiDataFetchHandler,
  isSsnHolder,
  isItinHolder,
}: {
  showInfo: boolean;
  setShowInfo: (showInfo: boolean) => void;
  piiDataFetchHandler: () => Promise<PIIData>;
  isSsnHolder?: boolean;
  isItinHolder?: boolean;
}) => {
  const [piiData, setPIIData] = useState<PIIData>({
    ssn: Resource.NORA_M_DASH,
    driverLicenseNumber: Resource.NORA_M_DASH,
    driverLicenseState: Resource.NORA_M_DASH,
    decryptedItin: Resource.NORA_M_DASH,
    emailAddress: Resource.NORA_M_DASH,
    phoneNumber: Resource.NORA_M_DASH,
  });

  async function toggleVisibility() {
    setShowInfo?.(!showInfo);
    if (!showInfo) {
      setPIIData(await piiDataFetchHandler());
    } else {
      setPIIData({
        ssn: Resource.NORA_M_DASH,
        driverLicenseNumber: Resource.NORA_M_DASH,
        driverLicenseState: Resource.NORA_M_DASH,
        decryptedItin: Resource.NORA_M_DASH,
        emailAddress: Resource.NORA_M_DASH,
        phoneNumber: Resource.NORA_M_DASH,
      });
    }
  }

  return (
    <div className={styles.Section}>
      <OverviewRow
        className={styles.Title}
        label={Resource.TASK_TITLE_PII_DATA}
      >
        <NoraButton
          type="button"
          name={ElementLocators.LOCATOR_SHOW_PII_DATA_BUTTON}
          onClick={toggleVisibility}
        >
          {showInfo ? Resource.HIDE_PII : Resource.SHOW_PII}
        </NoraButton>
      </OverviewRow>
      <OverviewRow label="Email Id">
        {!showInfo ? (
          Resource.HIDDEN_STRING_TEXT
        ) : piiData.emailAddress === Resource.NORA_M_DASH ? (
          <CircularProgress size="1.25em" />
        ) : piiData.emailAddress ? (
          <div>{`${piiData.emailAddress}`}</div>
        ) : (
          <div>{Resource.NORA_M_DASH}</div>
        )}
      </OverviewRow>
      {isSsnHolder || !isItinHolder ? (
        <OverviewRow label="SSN">
          {!showInfo ? (
            Resource.HIDDEN_STRING_TEXT
          ) : piiData.ssn === Resource.NORA_M_DASH ? (
            <CircularProgress size="1.25em" />
          ) : (
            piiData.ssn
          )}
        </OverviewRow>
      ) : (
        <OverviewRow label="ITIN">
          {!showInfo ? (
            Resource.HIDDEN_STRING_TEXT
          ) : piiData.decryptedItin === Resource.NORA_M_DASH ? (
            <CircularProgress size="1.25em" />
          ) : (
            <div>{`${piiData.decryptedItin}`}</div>
          )}
        </OverviewRow>
      )}
      <OverviewRow label="DL Number, State">
        {!showInfo ? (
          Resource.HIDDEN_STRING_TEXT
        ) : piiData.ssn === Resource.NORA_M_DASH ? (
          <CircularProgress size="1.25em" />
        ) : (
          <div>{`${piiData.driverLicenseNumber}, ${piiData.driverLicenseState}`}</div>
        )}
      </OverviewRow>
      <OverviewRow label="Phone Number">
        {!showInfo ? (
          Resource.HIDDEN_STRING_TEXT
        ) : piiData.phoneNumber === Resource.NORA_M_DASH ? (
          <CircularProgress size="1.25em" />
        ) : piiData.phoneNumber ? (
          <div>{`${piiData.phoneNumber}`}</div>
        ) : (
          <div>{Resource.NORA_M_DASH}</div>
        )}
      </OverviewRow>
    </div>
  );
};

export const PersonalInfoOverview = ({
  isOwnerVariant = false,
}: {
  isOwnerVariant?: boolean;
} = {}) => {
  const {
    age,
    nearestAge,
    gender,
    income,
    birthDate,
    birthState,
    birthCountry,
    stateAtSubmission,
    overviewTask,
    address,
    applicationId,
    occupation,
    termPolicyId,
    isSsnHolder,
    isItinHolder,
    citizenship,
    citizenshipDocumentType,
    householdIncome,
    userDisclosedVisaType,
  }: {
    age: number;
    nearestAge: number;
    gender: string;
    income: number;
    birthDate: string;
    birthState: string | null;
    birthCountry: string;
    stateAtSubmission: string;
    overviewTask: ITask | undefined;
    address: string | null;
    occupation: string;
    termPolicyId: number;
    applicationId: number;
    isSsnHolder: boolean | undefined;
    isItinHolder: boolean | undefined;
    citizenship: string | undefined;
    citizenshipDocumentType: string[] | undefined;
    householdIncome: number;
    identificationDocumentType: string[] | undefined;
    nonResidentUwControlMethod: string | undefined;
    userDisclosedVisaType: string | undefined;
  } = useSelector((state: RootState) => {
    return {
      occupation: state.application.occupation,
      age: state.application.age,
      gender: state.application.gender,
      termPolicyId: state.application.termPolicyId,
      applicationId: state.application.applicationId,
      nearestAge: state.application.nearestAge,
      income: state.application.income,
      birthDate: state.application.birthDate,
      birthState: state.application.birthState,
      birthCountry: state.application.birthCountry,
      stateAtSubmission: state.application.stateAtSubmission,
      overviewTask: getAllTasks(state.taskList).find(
        t => t.taskType === TaskTypes.OVERVIEW,
      ),
      address: state.application.address,
      bundledPolicyDetails: state.application.bundledPolicyDetails,
      isSsnHolder: state.application.isSsnHolder,
      isItinHolder: state.application.isItinHolder,
      citizenship: state.application.citizenship,
      citizenshipDocumentType: state.application.citizenshipDocumentType,
      householdIncome: state.application.householdIncome,
      identificationDocumentType: state.application.identificationDocumentType,
      nonResidentUwControlMethod: state.application.nonResidentUwControlMethod,
      userDisclosedVisaType: state.application.userDisclosedVisaType,
    };
  }, shallowEqual);

  const [showInfo, setShowInfo] = useState(false);
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const canViewPii = useAccess({
    permission: NoraPermissionV2.VIEW_PII_DATA,
    legacyCheck: canViewPIIData,
  });

  if (!overviewTask) {
    return null;
  }

  let birthLocation;
  if (birthState) {
    birthLocation = `${birthState}, ${birthCountry}`;
  } else {
    birthLocation = birthCountry;
  }

  const piiDataFetchHandler = async (): Promise<PIIData> => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      getApplicationPIIDataEndPoint(applicationId, termPolicyId),
    );
    if (data) {
      if (!data.error) {
        const applicationPIIData = data.parsedBody;
        return applicationPIIData;
      } else {
        setSnacks([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
    return {
      ssn: Resource.NORA_M_DASH,
      driverLicenseNumber: Resource.NORA_M_DASH,
      driverLicenseState: Resource.NORA_M_DASH,
      decryptedItin: Resource.NORA_M_DASH,
      emailAddress: Resource.NORA_M_DASH,
      phoneNumber: Resource.NORA_M_DASH,
    };
  };

  return (
    <div
      className={styles.Section}
      data-label={'overview_section'}
      data-section-id={'Overview'}
      id="PerInfo"
    >
      <FlexRow id={overviewTask.id.toString()} className={styles.Title}>
        <TitleSmall>{'Personal Information'}</TitleSmall>
      </FlexRow>

      <OverviewRow
        label={'Birthdate'}
        info={dateToString(birthDate) + ', ' + getAge(age, nearestAge, birthDate)}
      />

      <OverviewRow label={'Gender'} info={gender || Resource.NORA_M_DASH} />
      {occupation && (
        <OverviewRow
          label={'Occupation'}
          info={capitalizeFirstLetter(occupation) || Resource.NORA_M_DASH}
        />
      )}
      {!isOwnerVariant && (
        <OverviewRow
          label={'Personal Income'}
          info={getIncome(income) || Resource.NORA_M_DASH}
        />
      )}
      {!isOwnerVariant && householdIncome ? (
        <OverviewRow
          label={'Household Income'}
          info={getIncome(householdIncome) || Resource.NORA_M_DASH}
        />
      ) : null}
      <OverviewRow
        label={'Birthplace'}
        info={birthLocation || Resource.NORA_M_DASH}
      />
      <OverviewRow
        label={'State at Submission'}
        info={stateAtSubmission || Resource.NORA_M_DASH}
      />

      <OverviewRow label={'Address'} info={address || Resource.NORA_M_DASH} />

      {citizenship && (
        <OverviewRow
          label={'Citizenship'}
          info={customCitizenshipDisplay.get(citizenship)}
        />
      )}

      {userDisclosedVisaType && (
        <OverviewRow label={'VISA Type'} info={userDisclosedVisaType} />
      )}
      {citizenshipDocumentType && citizenshipDocumentType.length > 0 && (
        <OverviewRow
          label={'Document type'}
          info={`${citizenshipDocumentType[0].toUpperCase()}`}
        />
      )}

      {canViewPii && (
        <PIIDataRow
          showInfo={showInfo}
          setShowInfo={setShowInfo}
          piiDataFetchHandler={piiDataFetchHandler}
          isSsnHolder={isSsnHolder}
          isItinHolder={isItinHolder}
        />
      )}
    </div>
  );
};
const getBeneficiaryInitials = (
  firstName?: string,
  lastName?: string,
  fullName?: string,
): string => {
  if (firstName || lastName) {
    return [firstName, lastName]
      .filter(Boolean)
      .map(n => n![0].toUpperCase())
      .join('');
  }
  const trimmed = fullName?.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' ').filter(Boolean);
  const first = parts[0];
  const last = parts.length > 1 ? parts[parts.length - 1] : null;
  return [first, last]
    .filter(Boolean)
    .map(n => n![0].toUpperCase())
    .join('');
};
export const PolicyInfoOverview = () => {
  const {
    creationDate,
    carrierPolicyNumber,
    carrier,
    product,
    subCategory,
    overviewTask,
    applicationId,
    bundledPolicyDetails,
    beneficiaryDetails,
  }: {
    creationDate: string;
    carrierPolicyNumber: string;
    carrier: string;
    product: string;
    subCategory: string;
    overviewTask: ITask | undefined;
    applicationId: number | null;
    termPolicyId: number;
    bundledPolicyDetails: any;
    beneficiaryDetails: BeneficiaryDetailsDto[];
  } = useSelector((state: RootState) => {
    return {
      creationDate: state.application.creationDate,
      carrierPolicyNumber: state.application.carrierPolicyNumber,
      carrier: state.application.carrier,
      product: state.application.product,
      subCategory: state.application.subCategory,
      overviewTask: getAllTasks(state.taskList).find(
        t => t.taskType === TaskTypes.OVERVIEW,
      ),
      applicationId: state.application.applicationId,
      termPolicyId: state.application.termPolicyId,
      bundledPolicyDetails: state.application.bundledPolicyDetails,
      citizenship: state.application.citizenship,
      citizenshipDocumentType: state.application.citizenshipDocumentType,
      beneficiaryDetails: state.application.beneficiaryDetails,
    };
  }, shallowEqual);

  if (!overviewTask) {
    return null;
  }
  const decisions = useSelector(state => state.decision.decisions);
  const [piaReasons, setPiaReasons] = useState([]);
  const [policyData, setPolicyData] = useState<any>(null);

  const auth = useContext(AuthContext);
  const isNonCarrierRole = isNonCarrierRolePresent(auth?.roles);
  useEffect(() => {
    (async () => {
      const data = await HttpService.getInstance().get(
        `applications/${applicationId}/policydata`,
      );
      setPolicyData(data.parsedBody);

      const reasons = await HttpService.getInstance().get(
        `applications/${applicationId}/piaReasons`,
      );
      setPiaReasons(reasons.parsedBody);
    })();
  }, []);

  const appliedFor = decisions?.find(x => x.decisionType === DecisionType.AF);
  const appliedForStr: string[] = [];
  if (appliedFor && appliedFor?.rateClass) {
    appliedForStr.push(transformRateClass(appliedFor?.rateClass));
  }
  if (appliedFor && appliedFor?.tobaccoClass) {
    appliedForStr.push(transformTobaccoClass(appliedFor?.tobaccoClass));
  }

  return (
    <div
      className={styles.Section}
      data-label={'overview_section'}
      data-section-id={'Overview'}
    >
      <FlexRow id={overviewTask.id.toString()} className={styles.Title}>
        <TitleSmall>{'Policy Information'}</TitleSmall>
      </FlexRow>
      <OverviewRow
        label={'Date Created'}
        info={getFormattedTimestamp(creationDate)}
      />
      <OverviewRow
        label={'Carrier Policy Number'}
        info={carrierPolicyNumber || Resource.NORA_M_DASH}
      />
      <ProductRow
        label={'Product'}
        carrier={carrier}
        product={product}
        subCategory={subCategory}
        bundledPolicy={bundledPolicyDetails}
      />

      <OverviewRow label={'Carrier'} info={carrier || Resource.NORA_M_DASH} />

      {isNonCarrierRole && (
        <OverviewRow
          label={'Marketing Channel'}
          info={
            policyData?.channel?.trim()?.toLowerCase() === 'partnerships'
              ? `Partnerships${
                  policyData?.rateScale
                    ? ` - ${String(policyData?.rateScale).trim().toUpperCase()}`
                    : ''
                }`
              : policyData?.channel || 'Unknown'
          }
        />
      )}

      <PolicyStatus applicationId={applicationId} />

      {piaReasons?.length > 0 && (
        <OverviewRow
          label={'PIA Reasons'}
          info={piaReasons?.join(', ') || Resource.NORA_M_DASH}
        />
      )}
      {beneficiaryDetails?.length > 0 && (
        <>
          <div className={styles.BeneficiariesHeader}>Beneficiaries</div>
          {beneficiaryDetails.map((beneficiary, index) => {
            const nameParts = [
              beneficiary.firstName,
              beneficiary.middleName,
              beneficiary.lastName,
            ].filter(Boolean);
            const fullName =
              nameParts.length > 0
                ? nameParts.join(' ')
                : (beneficiary.name ?? Resource.NORA_M_DASH);
            const initials = getBeneficiaryInitials(
              beneficiary.firstName,
              beneficiary.lastName,
              beneficiary.name,
            );
            return (
              <React.Fragment key={index}>
                <div className={styles.BeneficiaryRow}>
                  <div className={styles.BeneficiaryAvatar}>
                    {initials || '?'}
                  </div>
                  <div className={styles.BeneficiaryInfo}>
                    <div className={styles.BeneficiaryNameLine}>
                      <span className={styles.BeneficiaryName}>{fullName}</span>
                      <span className={styles.BeneficiaryShare}>
                        {beneficiary.share}%
                      </span>
                    </div>
                    <div className={styles.BeneficiaryMeta}>
                      {beneficiary.relationships}
                      {beneficiary.birthDate
                        ? ` · b. ${beneficiary.birthDate}`
                        : ''}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};
export const RiskInfoOverview = () => {
  const {
    height,
    weight,
    bmi,
    totalCoverageInForce,
    hasAppliedInLast12Months,
    isWeightGainOrLoss,
    substanceLastUseDate,
    marijuanaUse,
    marijuanaUseDetails,
    cbdUse,
    thcUse,
    thcDetails,
    hasFamilyDiseaseHistory,
    hasCriminalRecord,
    avocation,
    foreignTravel,
    military,
    alcoholUsage,
    alcoholUsageDetails,
    trlScore,
    inUSForPast18Months,
    trlPreQualScore,
  }: {
    height: number;
    weight: number;
    bmi: number;
    totalCoverageInForce: number;
    hasAppliedInLast12Months: boolean | undefined;
    isWeightGainOrLoss: boolean | undefined;
    substanceLastUseDate: boolean | undefined;
    marijuanaUse: boolean | undefined;
    marijuanaUseDetails: FactAttributeDetail[] | null;
    cbdUse: boolean | undefined;
    thcUse: boolean | undefined;
    thcDetails: FactAttributeDetail[] | null | undefined;
    hasFamilyDiseaseHistory: boolean | undefined;
    hasCriminalRecord: boolean | undefined;
    avocation: boolean | undefined;
    foreignTravel: string[] | [];
    military: boolean | undefined;
    alcoholUsage: boolean | undefined;
    alcoholUsageDetails: FactAttributeDetail[] | null;
    trlScore: number | undefined;
    inUSForPast18Months: boolean | undefined;
    trlPreQualScore: number | undefined;
  } = useSelector((state: RootState) => {
    return {
      bmi: state.application.bmi,
      totalCoverageInForce: state.application.totalCoverageInForce,
      height: state.application.height,
      weight: state.application.weight,
      hasAppliedInLast12Months: state.application.hasAppliedInLast12Months,
      isWeightGainOrLoss: state.application.isWeightGainOrLoss,
      substanceLastUseDate: state.application.substanceLastUseDate,
      marijuanaUse: state.application.marijuanaUse,
      marijuanaUseDetails: state.application.marijuanaUseDetails,
      cbdUse: state.application.cbdUse,
      thcUse: state.application.thcUse,
      thcDetails: state.application.thcDetails as
        | FactAttributeDetail[]
        | null
        | undefined,
      hasFamilyDiseaseHistory: state.application.hasFamilyDiseaseHistory,
      hasCriminalRecord: state.application.hasCriminalRecord,
      avocation: state.application.avocation,
      foreignTravel: state.application.foreignTravel,
      military: state.application.military,
      alcoholUsage: state.application.alcoholUsage,
      alcoholUsageDetails: state.application.alcoholUsageDetails,
      trlScore: state.application.trlScore,
      inUSForPast18Months: state.application.inUSForPast18Months,
      trlPreQualScore: state.application.trlPreQualScore,
    };
  }, shallowEqual);
  const decisions = useSelector(state => state.decision.decisions);
  const bmiDisplay = bmi ?? Resource.NORA_M_DASH;
  const heightDisplay =
    Number.isFinite(Number(height)) && height !== null
      ? getFormatedHeight(height)
      : Resource.NORA_M_DASH;
  const weightDisplay =
    Number.isFinite(Number(weight)) && weight !== null
      ? `${weight}lbs`
      : Resource.NORA_M_DASH;
  const appliedFor = decisions?.find(x => x.decisionType === DecisionType.AF);
  const appliedForStr: string[] = [];
  if (appliedFor && appliedFor?.rateClass) {
    appliedForStr.push(transformRateClass(appliedFor?.rateClass));
  }
  if (appliedFor && appliedFor?.tobaccoClass) {
    appliedForStr.push(transformTobaccoClass(appliedFor?.tobaccoClass));
  }

  const auth = useContext(AuthContext);
  const shouldShowPrequalScore =
    auth?.userOrganization === UserOrganization.ETHOS;

  const etohDetails = alcoholUsage
    ? buildRiskDetailItems(alcoholUsageDetails ?? [])
    : [];
  const mjDetails = marijuanaUse
    ? buildRiskDetailItems(marijuanaUseDetails ?? [])
    : [];
  const thcDetailItems = thcUse
    ? buildRiskDetailItems((thcDetails ?? []) as FactAttributeDetail[])
    : [];

  return (
    <div
      className={styles.Section}
      data-label={'overview_section'}
      data-section-id={'Overview'}
    >
      <FlexRow id={'a'} className={styles.Title}>
        <TitleSmall>{'Risk Details'}</TitleSmall>
      </FlexRow>
      {!isTheBoolEmpty(hasAppliedInLast12Months) && (
        <OverviewRow
          label={'Recent Application'}
          info={booleanToYesNo(hasAppliedInLast12Months)}
        />
      )}
      {totalCoverageInForce && (
        <OverviewRow
          label={'Inforce Coverage'}
          info={`${totalCoverageInForce}`}
        />
      )}
      {trlScore && <OverviewRow label={'TRL'} info={trlScore} />}
      <OverviewRow
        label={'BMI, Height, Weight'}
        info={`${bmiDisplay}, ${heightDisplay},  ${weightDisplay}`}
      />
      {shouldShowPrequalScore && (
        <OverviewRow
          label={'PreQual'}
          info={trlPreQualScore ?? Resource.NORA_M_DASH}
        />
      )}
      {!isTheBoolEmpty(isWeightGainOrLoss) && (
        <OverviewRow
          label={'Weight Loss or Gain'}
          info={booleanToYesNo(isWeightGainOrLoss)}
        />
      )}
      {substanceLastUseDate && (
        <OverviewRow
          label={'Last tobacco used'}
          info={dateToString(
            substanceLastUseDate,
            DATE_FORMAT_ONLY_MONTH_AND_YEAR,
          )}
        />
      )}
      {!isTheBoolEmpty(alcoholUsage) && (
        <RiskDetailRow
          label={'ETOH'}
          value={alcoholUsage}
          details={etohDetails}
        />
      )}
      {!isTheBoolEmpty(hasFamilyDiseaseHistory) && (
        <OverviewRow
          label={'Family history'}
          info={booleanToYesNo(hasFamilyDiseaseHistory)}
        />
      )}
      {!isTheBoolEmpty(avocation) && (
        <OverviewRow label={'Avocation'} info={booleanToYesNo(avocation)} />
      )}
      {!isTheBoolEmpty(marijuanaUse) && (
        <RiskDetailRow label={'MJ'} value={marijuanaUse} details={mjDetails} />
      )}
      {!isTheBoolEmpty(cbdUse) && (
        <OverviewRow label={'CBD'} info={booleanToYesNo(cbdUse)} />
      )}
      {!isTheBoolEmpty(thcUse) && (
        <RiskDetailRow
          label={'THC'}
          value={thcUse}
          details={thcDetailItems}
        />
      )}
      {foreignTravel && (
        <OverviewRow
          label={'Foreign Travel'}
          info={
            foreignTravel.length === 0
              ? Resource.NORA_M_DASH
              : foreignTravel.map(capitalizeFirstLetter).join(', ')
          }
        />
      )}
      {!isTheBoolEmpty(military) && (
        <OverviewRow label={'Military'} info={booleanToYesNo(military)} />
      )}
      {!isTheBoolEmpty(hasCriminalRecord) && (
        <OverviewRow
          label={'Criminal History'}
          info={booleanToYesNo(hasCriminalRecord)}
        />
      )}
      {!isTheBoolEmpty(inUSForPast18Months) && (
        <OverviewRow
          label={'In US past 18 months'}
          info={booleanToYesNo(inUSForPast18Months)}
        />
      )}
    </div>
  );
};

const getFormatedHeight = (height: number) => {
  const htFeet = Math.floor(height / 12);
  const htInch = height % 12;
  if (htFeet) {
    return `${htFeet}ft ${htInch}in`;
  } else {
    // For backward compatibility . we will remove this once we fix data in BE
    return `${Math.floor(height)}ft ${Math.round(
      (Number(height) * 12) % 12,
    )}in`;
  }
};

const getIncome = (income: number) => {
  return income && Number.isFinite(Number(income))
    ? `$${income.toLocaleString()}`
    : Resource.NORA_M_DASH;
};
